import {
  useState,
  useEffect,
  Fragment,
  createContext,
  useContext,
} from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button.jsx";
import { Input } from "@/components/ui/input.jsx";
import { Label } from "@/components/ui/label.jsx";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card.jsx";
import { Alert, AlertDescription } from "@/components/ui/alert.jsx";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog.jsx";
import { useAuth } from "@/lib/context/AuthContext.jsx";
import { useToast } from "@/hooks/use-toast.js";
import { Building2, Search, Loader2, Check } from "lucide-react";
import routes from "@/routes/routes";
import { api } from "@/lib/api/api";

// Form validation schema
const createAccountSchema = z.object({
  key: z.string(),
});

const CreateAccountContext = createContext(null);

function CreateAccountProvider({ children }) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { refreshUser } = useAuth();

  const [isDetecting, setIsDetecting] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [detectedCompany, setDetectedCompany] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isCheckingUser, setIsCheckingUser] = useState(true);

  const form = useForm({
    resolver: zodResolver(createAccountSchema),
    defaultValues: {
      key: "",
    },
  });

  // Watch SIREN field for real-time validation
  const keyValue = form.watch("key");

  // Check if user already has accounts
  const checkUserAccounts = async () => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL || "http://localhost:8000/api"}/users/me/`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error("Erreur lors de la vérification du profil utilisateur");
      }

      const userData = await response.json();

      // If user has accounts, redirect to companies page
      if (userData.accounts && userData.accounts.length > 0) {
        toast({
          title: "Compte déjà existant",
          description:
            "Vous avez déjà un compte. Redirection vers la liste des entreprises...",
        });
        navigate("/societes", { replace: true });
        return;
      }
    } catch (error) {
      console.error("User check failed:", error);
      toast({
        title: "Erreur de vérification",
        description: "Impossible de vérifier votre profil. Veuillez réessayer.",
        variant: "destructive",
      });
    } finally {
      setIsCheckingUser(false);
    }
  };

  // Check user accounts on component mount
  useEffect(() => {
    checkUserAccounts();
  }, []);

  const handleSirenDetection = async () => {
    const keyValue = form.getValues("key");

    setIsDetecting(true);

    const response = await api.searchUserCompany(
      keyValue.replace(/\s+/g, ""),
    );

    if (!response.ok) {
      toast({
        title: "Erreur de détection",
        description: "Impossible de détecter l'entreprise.",
        variant: "destructive",
      });
      setIsDetecting(false);
      return;
    }

    const searchResults = await response.json();
    if (searchResults.length > 0) {
      setDetectedCompany(searchResults[0]);
    }
    setShowConfirmation(true);
    setIsDetecting(false);
  };

  const handleCreateAccount = async () => {
    if (!detectedCompany) return;

    setIsCreating(true);

    const response = await api.createUserCompany(detectedCompany);
    if (!response.ok) {
      toast({
        title: "Erreur de création",
        description: "Impossible de créer l'entreprise",
        variant: "destructive",
      });
      setIsCreating(false);
      return;
    }

    setIsCreating(false);

    toast({
      title: "Entreprise créé avec succès",
      description: "Votre entreprise a bien été ajoutée à votre compte",
    });

    window.location.reload();
  };

  const value = {
    form,
    isDetecting,
    detectedCompany,
    isCreating,
    showConfirmation,
    setShowConfirmation,
    isCheckingUser,
    keyValue,
    handleSirenDetection,
    handleCreateAccount,
  };

  return (
    <CreateAccountContext.Provider value={value}>
      {children}
    </CreateAccountContext.Provider>
  );
}

function useCreateAccount() {
  return useContext(CreateAccountContext);
}

export default function CreateAccount() {
  const { refreshUser, userCompany, loadingUserCompany } = useAuth();

  if (loadingUserCompany) return <></>;
  if (userCompany) return <Navigate to={"/"} replace />
  return (
    <CreateAccountProvider>
      <CreateAccountComponent />
    </CreateAccountProvider>
  );
}

function CreateAccountComponent() {
  const { isCheckingUser } = useCreateAccount();

  // Show loading state while checking user accounts
  if (isCheckingUser) return <CreateAccountLoading />;

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-2xl">
        <CardHeader className="text-center space-y-2">
          <CreateAccountHeader />
        </CardHeader>

        <CardContent>
          <CreateAccountForm />
        </CardContent>
      </Card>

      <CreateAccountConfirmationDialog />
    </div>
  );
}

function CreateAccountHeader() {
  return (
    <Fragment>
      <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
        <Building2 className="h-6 w-6 text-primary" />
      </div>
      <CardTitle className="text-2xl font-bold">Votre entreprise</CardTitle>
      <p className="text-muted-foreground">
        Renseignez les informations de votre entreprise afin de l'identifier
      </p>
    </Fragment>
  );
}

function CreateAccountLoading() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardContent className="pt-6">
          <div className="flex flex-col items-center justify-center space-y-4">
            <Loader2 className="h-8 w-8 animate-spin" />
            <p className="text-muted-foreground">
              Vérification de votre profil...
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function CreateAccountForm() {
  const { form, isDetecting, detectedCompany, keyValue, handleSirenDetection } =
    useCreateAccount();

  const navigate = useNavigate();

  return (
    <form className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor="key">SIREN</Label>
        <div className="flex gap-2">
          <Input
            id="key"
            type="text"
            placeholder="123456789"
            inputMode="numeric"
            {...form.register("key")}
            className={form.formState.errors.key ? "border-destructive" : ""}
            disabled={isDetecting}
          />
          <Button
            type="button"
            onClick={handleSirenDetection}
            disabled={isDetecting || keyValue.replace(/\D/g, "").length !== 9}
          >
            {isDetecting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Détection...
              </>
            ) : (
              <>
                <Search className="h-4 w-4 mr-2" />
                Détecter
              </>
            )}
          </Button>
        </div>
        {form.formState.errors.key && (
          <p className="text-sm text-destructive">
            {form.formState.errors.key.message}
          </p>
        )}
      </div>

      {detectedCompany && (
        <Alert className="border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950">
          <Check className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-700 dark:text-green-300">
            <div className="space-y-2">
              <p className="font-medium">Entreprise détectée:</p>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="font-medium">Nom:</span>{" "}
                  {detectedCompany.name}
                </div>
                {/* <div>
                  <span className="font-medium">SIREN:</span>{" "}
                  {detectedCompany.identifier}
                </div>
                <div>
                  <span className="font-medium">Code APE:</span>{" "}
                  {detectedCompany.activity_code}
                </div>
                <div>
                  <span className="font-medium">Secteur:</span>{" "}
                  {detectedCompany.activities_sector || "Non disponible"}
                </div>
                <div className="col-span-2">
                  <span className="font-medium">Adresse:</span>{" "}
                  {detectedCompany.address}, {detectedCompany.zip_code}{" "}
                  {detectedCompany.town}
                </div> */}
              </div>
            </div>
          </AlertDescription>
        </Alert>
      )}

      <div className="flex gap-4">
        <Button
          type="button"
          variant="outline"
          onClick={() => navigate("/login")}
          disabled={isDetecting}
        >
          Retour
        </Button>
      </div>
    </form>
  );
}

function CreateAccountConfirmationDialog() {
  const {
    isCreating,
    detectedCompany,
    showConfirmation,
    setShowConfirmation,
    handleCreateAccount,
  } = useCreateAccount();

  return (
    <AlertDialog open={showConfirmation} onOpenChange={setShowConfirmation}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            Confirmer les informations de l'entreprise
          </AlertDialogTitle>
          <AlertDialogDescription>
            Vérifiez que les informations détectées correspondent à votre
            entreprise avant de créer le compte.
          </AlertDialogDescription>
        </AlertDialogHeader>
        {detectedCompany && (
          <div className="space-y-3 py-4">
            <div className="text-sm space-y-2">
              <div>
                <span className="font-medium">Nom de l'entreprise:</span>{" "}
                {detectedCompany.name}
              </div>
              {/* <div>
                <span className="font-medium">SIREN:</span>{" "}
                {detectedCompany.identifier}
              </div>
              {detectedCompany.siret_number && (
                <div>
                  <span className="font-medium">SIRET:</span>{" "}
                  {detectedCompany.siret_number}
                </div>
              )}
              {detectedCompany.activity_code && (
                <div>
                  <span className="font-medium">Code APE:</span>{" "}
                  {detectedCompany.activity_code}
                </div>
              )}
              <div>
                <span className="font-medium">Secteur d'activité:</span>{" "}
                {detectedCompany.activities_sector || "Non disponible"}
              </div>
              <div>
                <span className="font-medium">Adresse:</span>{" "}
                {detectedCompany.address}, {detectedCompany.zip_code}{" "}
                {detectedCompany.town}
              </div>
              {detectedCompany.immatriculation_date && (
                <div>
                  <span className="font-medium">Date d'immatriculation:</span>{" "}
                  {new Date(
                    detectedCompany.immatriculation_date,
                  ).toLocaleDateString("fr-FR")}
                </div>
              )} */}
            </div>
          </div>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isCreating}>
            Ce n'est pas mon entreprise
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleCreateAccount}
            disabled={isCreating}
          >
            {isCreating ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Création...
              </>
            ) : (
              "Confirmer et créer le compte"
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
